import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Box, Typography, Paper, Button, Stack, CircularProgress, Alert, Grid, Card, CardContent, CardActions, IconButton, ToggleButton, ToggleButtonGroup } from '@mui/material';
import useCVList from 'features/feature_cv/hooks/useCVList';
import { HttpService } from 'core/services/http';
import { CVHttpService } from 'features/feature_cv/services';
import { Utils } from 'core/utils';
import { useQueryClient, useMutation } from 'react-query';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ShareIcon from '@mui/icons-material/Share';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DescriptionIcon from '@mui/icons-material/Description';

const formatText = (value, fallback = '—') => {
    if (!Utils.normalizeText) return value ?? fallback;
    return Utils.normalizeText(value, fallback);
};

const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
        });
    } catch {
        return dateString;
    }
};

function JobseekerCVsPage() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [viewMode, setViewMode] = useState('list');

    const { data, isLoading, error } = useCVList(
        { size: 30 },
        { enabled: true, retry: false }
    );

    const deleteMutation = useMutation(
        (id) => CVHttpService.deleteCV(id),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('cvList');
            },
            onError: (err) => {
                alert('Failed to delete CV');
            }
        }
    );

    const handleCreateNew = () => {
        navigate('/cv/builder');
    };

    const handleCreateWithAI = () => {
        navigate('/cv/ai-builder');
    };

    const handleView = (cvId) => {
        navigate(`/cv/${cvId}`);
    };

    const handleEdit = (cvId) => {
        navigate(`/cv/builder/${cvId}`);
    };

    const handleDelete = (cvId, e) => {
        e.stopPropagation();
        if (window.confirm('Are you sure you want to delete this CV?')) {
            deleteMutation.mutate(cvId);
        }
    };

    const handleDownloadPDF = async (cvId, e) => {
        e.stopPropagation();
        try {
            await CVHttpService.downloadPDF(cvId);
        } catch (err) {
            alert('Failed to download PDF');
        }
    };

    const handleShare = async (cv, e) => {
        e.stopPropagation();
        if (!cv.share_uuid) {
            alert('Shareable link not available');
            return;
        }
        const shareUrl = CVHttpService.getShareableURL(cv.share_uuid);
        try {
            await navigator.clipboard.writeText(shareUrl);
            alert('Shareable link copied to clipboard!');
        } catch (err) {
            alert('Failed to copy link');
        }
    };

    const cvs = data?.results || data || [];

    return (
        <Container maxWidth="lg">
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h5" fontWeight={700}>
                    My CVs
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={(e, newView) => {
                            if (newView !== null) {
                                setViewMode(newView);
                            }
                        }}
                        aria-label="view mode"
                        size="small"
                    >
                        <ToggleButton value="list" aria-label="list view">
                            <ViewListIcon />
                        </ToggleButton>
                        <ToggleButton value="grid" aria-label="grid view">
                            <ViewModuleIcon />
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Button
                        variant="outlined"
                        startIcon={<AutoAwesomeIcon />}
                        onClick={handleCreateWithAI}
                    >
                        Create with AI
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleCreateNew}
                    >
                        Create New CV
                    </Button>
                </Stack>
            </Box>

            {isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Alert severity="error">
                    Failed to load CVs. Please try again later.
                </Alert>
            ) : cvs.length === 0 ? (
                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 6, textAlign: 'center' }}>
                    <Box sx={{
                        width: 56, height: 56, borderRadius: '50%',
                        bgcolor: 'grey.100', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', mx: 'auto', mb: 2,
                    }}>
                        <DescriptionIcon sx={{ color: 'text.disabled' }} />
                    </Box>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>No CVs yet</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                        Create your first CV to get started
                    </Typography>
                    <Stack direction="row" spacing={1.5} justifyContent="center">
                        <Button variant="outlined" startIcon={<AutoAwesomeIcon />} onClick={handleCreateWithAI} size="small">
                            Create with AI
                        </Button>
                        <Button variant="contained" disableElevation startIcon={<AddIcon />} onClick={handleCreateNew} size="small">
                            Create New CV
                        </Button>
                    </Stack>
                </Paper>
            ) : viewMode === 'grid' ? (
                <Grid container spacing={2}>
                    {cvs.map((cv) => (
                        <Grid item xs={12} sm={6} md={4} key={cv.id}>
                            <Card
                                elevation={0}
                                sx={{
                                    height: '100%', display: 'flex', flexDirection: 'column',
                                    cursor: 'pointer',
                                    border: '2px solid transparent',
                                    outline: '1px solid',
                                    outlineColor: 'divider',
                                    borderRadius: 2,
                                    transition: 'outline-color 0.15s, box-shadow 0.15s',
                                    '&:hover': {
                                        outlineColor: 'primary.main',
                                        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                                        '& .cv-actions': { opacity: 1 },
                                    },
                                }}
                                onClick={() => handleView(cv.id)}
                            >
                                <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                                    <Typography variant="subtitle1" fontWeight={600} gutterBottom noWrap>
                                        {formatText(cv.title, 'Untitled CV')}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" gutterBottom noWrap>
                                        {formatText(cv.name)} {formatText(cv.last_name)}
                                    </Typography>
                                    <Typography variant="caption" color="text.disabled">
                                        Updated {formatDate(cv.updated_at)}
                                    </Typography>
                                </CardContent>
                                <CardActions className="cv-actions" sx={{ justifyContent: 'flex-end', pt: 0, opacity: 0, transition: 'opacity 0.15s' }}>
                                    <IconButton size="small" title="View" onClick={(e) => { e.stopPropagation(); handleView(cv.id); }}><VisibilityIcon fontSize="small" /></IconButton>
                                    <IconButton size="small" title="Edit" onClick={(e) => { e.stopPropagation(); handleEdit(cv.id); }}><EditIcon fontSize="small" /></IconButton>
                                    <IconButton size="small" title="PDF" onClick={(e) => { e.stopPropagation(); handleDownloadPDF(cv.id, e); }}><PictureAsPdfIcon fontSize="small" /></IconButton>
                                    <IconButton size="small" title="Share" onClick={(e) => { e.stopPropagation(); handleShare(cv, e); }}><ShareIcon fontSize="small" /></IconButton>
                                    <IconButton size="small" color="error" title="Delete" onClick={(e) => { e.stopPropagation(); handleDelete(cv.id, e); }}><DeleteIcon fontSize="small" /></IconButton>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                    {cvs.map((cv, idx) => (
                        <Box
                            key={cv.id}
                            onClick={() => handleView(cv.id)}
                            sx={{
                                px: 2.5, py: 2,
                                display: 'flex', alignItems: 'center', gap: 2,
                                cursor: 'pointer',
                                borderBottom: idx < cvs.length - 1 ? '1px solid' : 'none',
                                borderColor: 'divider',
                                '&:hover': {
                                    bgcolor: 'action.hover',
                                    '& .cv-actions': { opacity: 1 },
                                },
                                transition: 'background 0.15s',
                            }}
                        >
                            <Box sx={{
                                width: 36, height: 36, borderRadius: 1.5, bgcolor: 'primary.50',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            }}>
                                <DescriptionIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={600} noWrap>
                                    {formatText(cv.title, 'Untitled CV')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap display="block">
                                    {formatText(cv.name)} {formatText(cv.last_name)}
                                    {cv.updated_at ? ` · Updated ${formatDate(cv.updated_at)}` : ''}
                                </Typography>
                            </Box>
                            <Stack className="cv-actions" direction="row" spacing={0.5} sx={{ opacity: 0, transition: 'opacity 0.15s', flexShrink: 0 }}>
                                <IconButton size="small" title="View" onClick={(e) => { e.stopPropagation(); handleView(cv.id); }}><VisibilityIcon fontSize="small" /></IconButton>
                                <IconButton size="small" title="Edit" onClick={(e) => { e.stopPropagation(); handleEdit(cv.id); }}><EditIcon fontSize="small" /></IconButton>
                                <IconButton size="small" title="PDF" onClick={(e) => { e.stopPropagation(); handleDownloadPDF(cv.id, e); }}><PictureAsPdfIcon fontSize="small" /></IconButton>
                                <IconButton size="small" title="Share" onClick={(e) => { e.stopPropagation(); handleShare(cv, e); }}><ShareIcon fontSize="small" /></IconButton>
                                <IconButton size="small" color="error" title="Delete" onClick={(e) => { e.stopPropagation(); handleDelete(cv.id, e); }}><DeleteIcon fontSize="small" /></IconButton>
                            </Stack>
                        </Box>
                    ))}
                </Paper>
            )}
        </Container>
    );
}

export default JobseekerCVsPage;

